import { useEffect, useState } from 'react';

import { useParams, useSearchParams } from 'react-router-dom';

import * as tnaServices from '../../../../../services/tnaServices';
import { use } from 'react';
import Comparer from '../../../quaity/compliance/Comparer';

import css from './TnaDifferenceDetails.module.css';

/**
 * Page component that shows a side-by-side diff between two TNAs.
 *
 * The two TNAs to compare are identified by ?from=<id>&to=<id> query params.
 * Data is loaded in three cascading steps:
 *   1. All ATA chapters are fetched once on mount (used as the row basis).
 *   2. Both TNA header records are fetched in parallel when from/to change.
 *   3. All modules for each TNA are fetched once the headers are available.
 * A fourth effect consolidates modules + ATAs into one row per ATA chapter,
 * which drives the diff table.
 *
 * Each table cell uses the Comparer component to highlight text changes, or
 * shows "ND" (No Difference) when the two sides are identical.
 */
function TnaDifferenceDetails() {
    const [searchParams, setSearchParams] = useSearchParams(); // Get to and from tna IDs from query params
    const { id } = useParams(); // Get difference ID from route params

    // TNA header records for the "from" and "to" sides
    const [tnaFrom, setTnaFrom] = useState(null);
    const [tnaTo, setTnaTo] = useState(null);

    // Full module details for each side (array-of-arrays; each inner array is one module's API response)
    const [tnaFromModules, setTnaFromModules] = useState([]);
    const [tnaToModules, setTnaToModules] = useState([]);

    const [allAtas, setAllAtas] = useState([]); // The basis for consolidation
    const [consolidated, setConsolidated] = useState([]); // Consolidated data for display

    // TNA IDs parsed from the URL query string
    const from = searchParams.get('from'); // Get 'from' TNA ID from query params
    const to = searchParams.get('to'); // Get 'to' TNA ID from query params

    /**
     * Collects the 'components' text for a given ATA chapter across all modules
     * on one TNA side. Values from multiple modules are joined with a newline.
     */
    const combineComponents = (tnaModules, ataId) => {
        if (!tnaModules || tnaModules.length === 0) return '';
        const components = tnaModules.map((module) => {
            const element = module[0].ata?.find((a) => a.ataId === ataId);
            return element?.components;
        });

        return components.filter((x) => x).join('\n');
    };

    /**
     * Collects the 'objectives' text for a given ATA chapter across all modules
     * on one TNA side. Values from multiple modules are joined with a newline.
     */
    const combineObjectives = (tnaModules, ataId) => {
        if (!tnaModules || tnaModules.length === 0) return '';
        const objectives = tnaModules.map((module) => {
            const element = module[0].ata?.find((a) => a.ataId === ataId);
            return element?.objectives;
        });

        return objectives.filter((x) => x).join('\n');
    };

    /**
     * Returns the highest training level found for a given ATA chapter across
     * all modules on one TNA side. Level values are compared numerically.
     */
    const combineLevels = (tnaModules, ataId) => {
        if (!tnaModules || tnaModules.length === 0) return '';
        const levels = tnaModules.map((module) => {
            const element = module[0].ata?.find((a) => a.ataId === ataId);
            return element?.level;
        });

        const values = levels.filter((x) => x).map((n) => Number(n));
        return Math.max(...values).toString();
    };

    // Step 1 – fetch the master list of ATA chapters once on mount.
    // This list defines the rows of the diff table regardless of TNA content.
    useEffect(() => {
        // Fetch all ATAs on component mount
        tnaServices
            .getOption('getAllAtas')
            .then((res) => {
                setAllAtas(res.data || []);
            })
            .catch((err) => {
                console.error('Error fetching ATAs:', err);
            });
    }, []);

    // Step 4 – rebuild consolidated rows whenever modules or ATAs are updated.
    // Each row merges the from/to values for components, objectives, and level
    // for a single ATA chapter.
    useEffect(() => {
        // Consolidate data when modules or ATAs change
        if (tnaFromModules && tnaToModules && allAtas.length > 0) {
            const consolidatedData = allAtas.map((ata) => ({
                ataId: ata.ataId,
                ataNo: ata.ataNo,
                fromComponents: combineComponents(tnaFromModules, ata.ataId),
                toComponents: combineComponents(tnaToModules, ata.ataId),
                fromObjectives: combineObjectives(tnaFromModules, ata.ataId),
                toObjectives: combineObjectives(tnaToModules, ata.ataId),
                maxFromLevel: combineLevels(tnaFromModules, ata.ataId),
                maxToLevel: combineLevels(tnaToModules, ata.ataId),
            }));
            setConsolidated(consolidatedData);
        }
    }, [tnaFromModules, tnaToModules, allAtas]);

    // Step 2 – fetch both TNA header records in parallel whenever the IDs change.
    // The headers contain the module ID lists needed for step 3.
    useEffect(() => {
        // Fetch TNA details for 'from' and 'to' IDs
        const fetchData = async () => {
            try {
                const [fromRes, toRes] = await Promise.all([tnaServices.getById(from), tnaServices.getById(to)]);
                setTnaFrom(fromRes.data[0]);
                setTnaTo(toRes.data[0]);
            } catch (error) {
                console.error('Error fetching TNA difference details:', error);
            }
        };

        fetchData();
    }, [from, to]);

    // Step 3 – once TNA headers are available, fetch the full detail for each
    // module listed in the header. Both sides are loaded independently so that
    // one side being ready does not block the other.
    useEffect(() => {
        // Fetch modules for 'from' and 'to' TNAs when they are set
        const fetchDataFrom = async () => {
            try {
                const res = await Promise.all(
                    tnaFrom.modules.map(async (module) => tnaServices.getModuleById(module.tnaModuleId)),
                );
                setTnaFromModules(res.map((r) => r.data));
            } catch (err) {
                console.error('Error loading TNA "from" modules:', err);
            }
        };
        const fetchDataTo = async () => {
            try {
                const res = await Promise.all(
                    tnaTo.modules.map(async (module) => tnaServices.getModuleById(module.tnaModuleId)),
                );
                setTnaToModules(res.map((r) => r.data));
            } catch (err) {
                console.error('Error loading TNA "to" modules:', err);
            }
        };

        if (tnaFrom) {
            fetchDataFrom();
        }
        if (tnaTo) {
            fetchDataTo();
        }
    }, [tnaFrom, tnaTo]);

    return (
        <div>
            <h2>TNA Difference Details</h2>
            {/* One row per ATA chapter; columns show components, objectives, and level diffs */}
            <table className={css.table}>
                <thead className={css.thead}>
                    <tr>
                        <th className={css.th}>ATA</th>
                        {/* <th className={css.th}>From</th>
                        <th className={css.th}>To</th> */}
                        <th className={css.th}>Component</th>
                        <th className={css.th}>Objectives</th>
                        <th className={css.th}>Levels</th>
                    </tr>
                </thead>
                <tbody>
                    {consolidated.map((ata) => (
                        <tr key={ata.ataId}>
                            <td className={css.td}>
                                <div className={css.content}>{ata.ataNo}</div>
                            </td>
                            {/* Raw from/to columns hidden; Comparer handles the visual diff instead */}
                            {/* <td className={css.td}><div className={css.content}>{ata.fromComponents}</div></td>
                            <td className={css.td}><div className={css.content}>{ata.toComponents}</div></td> */}
                            <td className={css.td}>
                                <div className={css.content}>
                                    {/* localeCompare returns 0 (falsy) when strings are identical → show ND */}
                                    {ata.fromComponents.localeCompare(ata.toComponents) ? (
                                        <Comparer oldString={ata.fromComponents} newString={ata.toComponents} />
                                    ) : (
                                        <div className={css.nd}>ND</div>
                                    )}
                                </div>
                            </td>
                            <td className={css.td}>
                                <div className={css.content}>
                                    {ata.fromObjectives.localeCompare(ata.toObjectives) ? (
                                        <Comparer oldString={ata.fromObjectives} newString={ata.toObjectives} />
                                    ) : (
                                        <div className={css.nd}>ND</div>
                                    )}
                                </div>
                            </td>
                            <td className={css.td}>
                                <div className={css.content}>
                                    {ata.maxFromLevel.localeCompare(ata.maxToLevel) ? (
                                        <Comparer oldString={ata.maxFromLevel} newString={ata.maxToLevel} />
                                    ) : (
                                        <div className={css.nd}>ND</div>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default TnaDifferenceDetails;

//File: src/components/03-main/training/tna/differences/TnaDifferencesOverview.jsx
